import React, { Suspense, lazy, useEffect, useState } from "react";
import { apiGet } from "@/lib/api";

import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import PillarStrip from "@/components/PillarStrip";
import JourneyTimeline from "@/components/JourneyTimeline";
import ToolsPreview from "@/components/ToolsPreview";
import AdvisorCard from "@/components/AdvisorCard";
import HowItWorks from "@/components/HowItWorks";
import ComplianceTrustStrip from "@/components/ComplianceTrustStrip";
import EducationHubPreview from "@/components/EducationHubPreview";
import FinalCTA from "@/components/FinalCTA";
import NextBestAction from "@/components/NextBestAction";
import Footer from "@/components/Footer";
import ExitIntentLeadMagnet from "@/components/ExitIntentLeadMagnet";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { trackPageView } from "@/lib/analytics";

/* Phase 12 — heavy charts lazy-loaded so they do not block first paint. */
const WealthCurveChart = lazy(() => import("@/components/WealthCurveChart"));
const ProtectionGapPreview = lazy(() =>
  import("@/components/ProtectionGapPreview"),
);
const CarrierStrip = lazy(() => import("@/components/CarrierStrip"));

const SectionFallback = ({ h = 400 }) => (
  <div
    aria-hidden="true"
    className="w-full bg-white"
    style={{ height: h, minHeight: h }}
  />
);

/**
 * Phase 12 — home narrative with carrier trust + data-viz layer:
 *  1.  Hero
 *  2.  Pillar Strip
 *  3.  Your Journey (now includes per-stage coverage-mix donut)
 *  4.  Wealth Curve chart (illustrative)
 *  5.  Interactive Tools preview
 *  6.  Protection Gap interactive preview
 *  7.  Meet Your Advisors
 *  8.  Our Carrier Partners (NEW)
 *  9.  How It Works
 * 10.  Compliance Trust Strip
 * 11.  Education Hub preview
 * 12.  Final CTA band
 * 13.  Footer
 */
export default function Landing() {
  const [products, setProducts] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [stages, setStages] = useState([]);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, a, s] = await Promise.all([
          apiGet("/products"),
          apiGet("/advisors"),
          apiGet("/life-stages"),
        ]);
        if (cancelled) return;
        setProducts(p || []);
        setAdvisors(a || []);
        setStages(s || []);
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to load Lifespan data", e);
          setLoadError("Unable to load content. Please refresh.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useScrollReveal();

  useEffect(() => {
    trackPageView();
  }, []);

  return (
    <div data-testid="landing-page" className="min-h-screen bg-lw-ivory">
      <Nav />

      <main>
        <Hero />

        <PillarStrip />

        <JourneyTimeline stages={stages} products={products} />

        <Suspense fallback={<SectionFallback h={620} />}>
          <WealthCurveChart />
        </Suspense>

        <ToolsPreview />

        <Suspense fallback={<SectionFallback h={520} />}>
          <ProtectionGapPreview />
        </Suspense>

        {/* Advisors */}
        <section
          id="advisors"
          data-testid="advisors-section"
          className="py-20 sm:py-28 bg-lw-ivory-warm"
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center max-w-2xl mx-auto mb-14 reveal">
              <span className="lw-eyebrow">Meet Your Advisors</span>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]">
                Four specialists, <span className="italic">one journey.</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-lw-muted">
                Each of our advisors leads a specific chapter of your financial
                life — so you always talk to the right person for the question.
              </p>
            </div>
            {loadError && (
              <p
                data-testid="advisors-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6"
              >
                {loadError}
              </p>
            )}
            <div
              data-testid="advisors-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6"
            >
              {advisors.map((a) => (
                <AdvisorCard key={a.id} advisor={a} />
              ))}
            </div>
          </div>
        </section>

        <Suspense fallback={<SectionFallback h={520} />}>
          <CarrierStrip />
        </Suspense>

        <HowItWorks />

        <ComplianceTrustStrip />

        <EducationHubPreview />

        <NextBestAction
          pageId="home"
          step={1}
          heading="Take the 60-second Journey quiz"
          rationale="You're just discovering us. In one minute, tell us your life stage and we'll surface the 2–3 products most relevant for you — no email, no commitment."
          primary={{ label: "Start the Journey quiz", to: "/tools/product-recommender", testid: "nba-home-primary" }}
          secondary={{ label: "Meet the team", to: "/#advisors", testid: "nba-home-secondary" }}
        />

        <FinalCTA />
      </main>

      <Footer />
      <ExitIntentLeadMagnet source="home_exit_intent" pageContext={{ page: 'home' }} />
    </div>
  );
}
